/**
 * User and Authentication Service
 * Handles user management and authentication
 */

import {
  User,
  UserRole,
  LoginFormData,
  SignupFormData,
  AuthResponse,
  Timezone,
  UserStatus,
} from '@/types'
import { BaseService } from './base.service'
import {
  StorageKeys,
  getItem,
  setItem,
  removeItem,
  simulateNetworkDelay,
  isStorageInitialized,
  markStorageInitialized,
  setAll,
} from './storage'

class UserService extends BaseService<User> {
  constructor() {
    super(StorageKeys.USERS)
  }

  /**
   * Login user
   */
  async login(credentials: LoginFormData): Promise<AuthResponse> {
    await simulateNetworkDelay()

    const users = await this.getAll()
    const user = users.find((u) => u.email === credentials.email)

    if (!user) {
      throw new Error('Invalid email or password')
    }

    // In a real app, this would verify hashed password
    // For prototype, we accept any password
    const token = this.generateToken(user)

    // Store auth token and current user
    setItem(StorageKeys.AUTH_TOKEN, token)
    setItem(StorageKeys.CURRENT_USER, user.id)

    return {
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
      },
      token,
    }
  }

  /**
   * Signup new user
   */
  async signup(data: SignupFormData): Promise<AuthResponse> {
    await simulateNetworkDelay()

    // Check if user already exists
    const users = await this.getAll()
    const existingUser = users.find((u) => u.email === data.email)

    if (existingUser) {
      throw new Error('User with this email already exists')
    }

    // Validate password match
    if (data.password !== data.confirmPassword) {
      throw new Error('Passwords do not match')
    }

    // Create new user
    const newUser = await this.create({
      name: data.name || data.email.split('@')[0],
      email: data.email,
      password: this.hashPassword(data.password),
      role: UserRole.SuperAdmin, // Default role
      timezone: Timezone.UTC,
      status: UserStatus.Online,
      workspaces: [],
      projects: [],
      tasks: [],
      messages: [],
      documents: [],
    })

    // If this is the first user (storage not initialized), initialize with empty data
    if (!isStorageInitialized()) {
      // Save only the new user to storage
      setAll(StorageKeys.USERS, [newUser])

      // Mark as initialized to prevent future seeding
      markStorageInitialized()
    } else {
      // Storage already initialized, just add the new user
      const allUsers = await this.getAll()
      const updatedUsers = [...allUsers, newUser]
      setAll(StorageKeys.USERS, updatedUsers)
    }

    // Auto-login
    const token = this.generateToken(newUser)
    setItem(StorageKeys.AUTH_TOKEN, token)
    setItem(StorageKeys.CURRENT_USER, newUser.id)

    return {
      user: {
        id: newUser.id,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
      },
      token,
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await simulateNetworkDelay()
    removeItem(StorageKeys.AUTH_TOKEN)
    removeItem(StorageKeys.CURRENT_USER)
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    const userId = getItem<string>(StorageKeys.CURRENT_USER)
    if (!userId) return null

    return this.getById(userId)
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return getItem<string>(StorageKeys.AUTH_TOKEN) !== null
  }

  /**
   * Get auth token
   */
  getToken(): string | null {
    return getItem<string>(StorageKeys.AUTH_TOKEN)
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<void> {
    await simulateNetworkDelay()

    const users = await this.getAll()
    const user = users.find((u) => u.email === email)

    if (user) {
      // TODO: Simulate sending reset email
    }
  }

  /**
   * Reset password
   */
  async resetPassword(_token: string, _newPassword: string): Promise<void> {
    await simulateNetworkDelay()

    // In real app, would verify token
    // Real implementation would decode token and update specific user
  }

  /**
   * Update user profile
   */
  async updateProfile(userId: string, data: Partial<User>): Promise<User> {
    return this.update(userId, data)
  }

  /**
   * Change password
   */
  async changePassword(
    userId: string,
    _currentPassword: string,
    newPassword: string
  ): Promise<void> {
    await simulateNetworkDelay()

    const user = await this.getById(userId)
    if (!user) {
      throw new Error('User not found')
    }

    // In real app, would verify current password
    // For prototype, just update
    await this.update(userId, {
      password: this.hashPassword(newPassword),
    } as Partial<User>)
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: UserRole): Promise<User[]> {
    return this.filter((user) => user.role === role)
  }

  /**
   * Get users by workspace
   */
  async getUsersByWorkspace(workspaceId: string): Promise<User[]> {
    return this.filter((user) => user.workspaces.some((w) => w.id === workspaceId))
  }

  /**
   * Search users by name or email
   */
  async searchUsers(query: string): Promise<User[]> {
    return this.search(query, ['name', 'email'])
  }

  /**
   * Generate auth token (simplified for prototype)
   */
  private generateToken(user: User): string {
    return `token_${user.id}_${Date.now()}`
  }

  /**
   * Hash password (simplified for prototype)
   */
  private hashPassword(password: string): string {
    return `hashed_${password}`
  }
}

export const userService = new UserService()
